import { useState } from 'react';
import type { CustomerManagerUsageDailyResponse } from '@nadoutong/contracts';
import {
  TrendBucketTable,
  TrendChart,
  TrendViewToggle,
} from '../../components/charts/TrendChart.tsx';
import type { TrendChartSeries, TrendView } from '../../components/charts/TrendChart.tsx';
import { formatCompact, formatUsdMicrosOrCentsAsCnyDisplay } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Chip, Panel } from '../../ui/index.ts';
import { cx } from '../../ui/cx.ts';
import {
  alignPrevious,
  bucketAxisLabel,
  bucketTimeLabel,
  deltaPercent,
  formatDelta,
  metricValue,
  seriesValues,
  sumThrough,
  type UsageTrendMetric,
  type UsageTrendMode,
} from './usageMetrics.ts';
import type { UsageTabWindow } from './usageWindows.ts';

const METRIC_KEYS: Record<UsageTrendMetric, I18nKey> = {
  usd: 'customer_manager_360.usage.trend.metric.usd',
  reqs: 'customer_manager_360.usage.trend.metric.reqs',
  tok: 'customer_manager_360.usage.trend.metric.tok',
};

const WINDOW_KEYS: Record<UsageTabWindow, I18nKey> = {
  today: 'customer_manager_360.usage.window.today',
  week: 'customer_manager_360.usage.window.week',
  month: 'customer_manager_360.usage.window.month',
  '7d': 'customer_manager_360.usage.window.7d',
  '30d': 'customer_manager_360.usage.window.30d',
  custom: 'customer_manager_360.usage.window.custom',
};

const WEEKDAY_KEYS = [
  'customer_manager_360.usage.weekday.0',
  'customer_manager_360.usage.weekday.1',
  'customer_manager_360.usage.weekday.2',
  'customer_manager_360.usage.weekday.3',
  'customer_manager_360.usage.weekday.4',
  'customer_manager_360.usage.weekday.5',
  'customer_manager_360.usage.weekday.6',
] as const satisfies readonly I18nKey[];

const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');

export function UsageTrendPanel({
  data,
  pill,
  calendar,
  interpolatePrevious,
  occurredIndex,
  dayFraction,
  nowAt,
  cnyPerUsd,
}: {
  readonly data: CustomerManagerUsageDailyResponse;
  readonly pill: UsageTabWindow;
  readonly calendar: boolean;
  readonly interpolatePrevious: boolean;
  readonly occurredIndex: number;
  readonly dayFraction: number;
  readonly nowAt: number | undefined;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const [metric, setMetric] = useState<UsageTrendMetric>('usd');
  const [mode, setMode] = useState<UsageTrendMode>('point');
  const [compareOn, setCompareOn] = useState(true);
  const [view, setView] = useState<TrendView>('chart');
  const weekday = (day: number): string => t(WEEKDAY_KEYS[day] ?? WEEKDAY_KEYS[0]);
  const previousDays = alignPrevious(data.days, data.previous.days);
  const currentPoint = data.days.map((day) => metricValue(day, metric));
  const previousPoint = previousDays.map((day) => metricValue(day, metric));
  const currentValues = seriesValues(data.days, metric, mode);
  const previousValues = seriesValues(previousDays, metric, mode);
  const currentLabels = data.days.map((day) => bucketTimeLabel(day.date, weekday));
  const previousLabels = previousDays.map((day) => bucketTimeLabel(day.date, weekday));
  const formatValue = (value: number): string => {
    if (metric === 'usd') {
      return formatUsdMicrosOrCentsAsCnyDisplay(value, Math.round(value / 10_000), cnyPerUsd, false);
    }
    if (metric === 'tok') return formatCompact(Math.round(value));
    return TOKEN_FORMATTER.format(Math.round(value));
  };
  const fraction = interpolatePrevious ? dayFraction : 1;
  const currentToNow = sumThrough(currentPoint, occurredIndex, 1);
  const previousToNow = sumThrough(previousPoint, occurredIndex, fraction);
  const previousFull = previousPoint.reduce((sum, value) => sum + value, 0);
  const delta = deltaPercent(currentToNow, previousToNow);
  const projection =
    calendar && previousToNow > 0 ? currentToNow * (previousFull / previousToNow) : null;
  const nowGap =
    nowAt !== undefined && compareOn && mode === 'cum' && delta !== null
      ? {
          currentValue: sumThrough(currentPoint, occurredIndex, 1),
          previousValue: previousToNow,
          percentLabel: formatDelta(delta),
          tone: (delta >= 0 ? 'ok' : 'bad') as 'ok' | 'bad',
        }
      : undefined;
  const currentSeries: TrendChartSeries = {
    key: 'current',
    label: t('customer_manager_360.usage.trend.series.current'),
    kind: 'line',
    tone: 'signal',
    values: currentValues,
    bucketLabels: currentLabels,
    fillArea: true,
    drawThroughIndex: occurredIndex,
  };
  const previousSeries: TrendChartSeries = {
    key: 'previous',
    label: t('customer_manager_360.usage.trend.series.previous'),
    kind: 'line',
    tone: 'neutral',
    values: previousValues,
    bucketLabels: previousLabels,
    dashed: true,
  };
  const chartSeries = compareOn ? [currentSeries, previousSeries] : [currentSeries];
  const baselineKey = calendar
    ? 'customer_manager_360.usage.compare.at_point'
    : 'customer_manager_360.usage.compare.same_span';
  const windowLabel = t(WINDOW_KEYS[pill]);
  const metricLabel = t(METRIC_KEYS[metric]);

  return (
    <Panel
      caption={t('customer_manager_360.usage.trend.title')}
      padded
      testId="customer-360-usage-trend"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {(['usd', 'reqs', 'tok'] as const).map((option) => {
            const active = option === metric;
            return (
              <Button
                key={option}
                variant={active ? 'secondary' : 'ghost'}
                size="sm"
                className={active ? 'bg-signal-dim text-signal-bright' : undefined}
                data-testid={`customer-360-usage-metric-${option}`}
                onClick={() => {
                  setMetric(option);
                }}
              >
                {t(METRIC_KEYS[option])}
              </Button>
            );
          })}
          <Seg
            ariaLabel={t('customer_manager_360.usage.trend.mode.point')}
            options={[
              { id: 'point', label: t('customer_manager_360.usage.trend.mode.point') },
              { id: 'cum', label: t('customer_manager_360.usage.trend.mode.cum') },
            ]}
            value={mode}
            testIdPrefix="customer-360-usage-mode"
            onChange={setMode}
          />
          <Seg
            ariaLabel={t('customer_manager_360.usage.trend.compare.on')}
            options={[
              { id: 'on', label: t('customer_manager_360.usage.trend.compare.on') },
              { id: 'off', label: t('customer_manager_360.usage.trend.compare.off') },
            ]}
            value={compareOn ? 'on' : 'off'}
            testIdPrefix="customer-360-usage-compare"
            onChange={(next) => {
              setCompareOn(next === 'on');
            }}
          />
          <TrendViewToggle
            view={view}
            onViewChange={setView}
            rollup="day"
            onRollupChange={() => undefined}
            testIdPrefix="customer-360-usage-trend"
            showRollup={false}
          />
        </div>
      }
    >
      <p className="m-0 mb-2 text-micro text-paper-mid">
        {t('customer_manager_360.usage.trend.legend.current', { window: windowLabel })}
        {compareOn ? ` · ${t('customer_manager_360.usage.trend.legend.previous')}` : ''}
        <span className="text-paper-low"> · {t('customer_manager_360.usage.trend.legend.now')}</span>
      </p>
      {view === 'chart' ? (
        <TrendChart
          labels={data.days.map((day) => bucketAxisLabel(day.date))}
          series={chartSeries}
          valueFormatter={formatValue}
          ariaLabel={t('customer_manager_360.usage.trend.aria')}
          emptyLabel={t('customer_manager_360.usage.trend.empty')}
          testId="customer-360-usage-trend-chart"
          nowAt={nowAt}
          occurredThroughIndex={occurredIndex}
          compare={compareOn ? { currentKey: 'current', previousKey: 'previous' } : undefined}
          hoverModeLabel={
            mode === 'cum'
              ? t('customer_manager_360.usage.trend.hover.mode.cum')
              : t('customer_manager_360.usage.trend.hover.mode.point')
          }
          nowGap={nowGap}
        />
      ) : (
        <TrendBucketTable
          bucketHeader={t('metrics.chart.col.bucket')}
          labels={currentLabels}
          columns={[
            {
              key: 'current',
              header: t('customer_manager_360.usage.trend.series.current'),
              cells: currentValues.map((value) => formatValue(value)),
            },
            ...(compareOn
              ? [
                  {
                    key: 'previous',
                    header: t('customer_manager_360.usage.trend.series.previous'),
                    cells: previousValues.map((value) => formatValue(value)),
                  },
                ]
              : []),
          ]}
          emptyLabel={t('customer_manager_360.usage.trend.empty')}
          testId="customer-360-usage-trend-table"
        />
      )}
      <div
        className="mt-3 grid grid-cols-2 border-t border-line-soft bg-well md:grid-cols-5"
        data-testid="customer-360-usage-cmpbar"
      >
        <CmpCell
          label={t('customer_manager_360.usage.trend.cmp.current', { window: windowLabel, metric: metricLabel })}
          value={formatValue(currentToNow)}
        />
        <CmpCell
          label={t('customer_manager_360.usage.trend.cmp.previous', { baseline: t(baselineKey) })}
          value={formatValue(previousToNow)}
          dim
        />
        <CmpCell
          label={t('customer_manager_360.usage.trend.cmp.delta')}
          value={delta === null ? '—' : formatDelta(delta)}
          tone={delta === null ? 'neutral' : delta >= 0 ? 'ok' : 'bad'}
        />
        <CmpCell
          label={t('customer_manager_360.usage.trend.cmp.previous_full')}
          value={formatValue(previousFull)}
          dim
        />
        {calendar ? (
          <div className="border-r border-line-soft px-3.5 py-2.5 last:border-r-0">
            <div className="flex items-center gap-1.5 text-micro text-paper-low">
              {t('customer_manager_360.usage.trend.cmp.projection')}
              <Chip tone="warn" lamp={false} testId="customer-360-usage-projection-chip">
                {t('customer_manager_360.usage.trend.cmp.projection_estimate')}
              </Chip>
            </div>
            <div
              className="mt-0.5 font-mono text-body text-warn"
              data-testid="customer-360-usage-projection"
            >
              {projection === null ? '—' : formatValue(projection)}
            </div>
          </div>
        ) : (
          <div className="px-3.5 py-2.5" data-testid="customer-360-usage-no-projection">
            <div className="text-micro text-paper-low">{t('customer_manager_360.usage.trend.cmp.no_projection')}</div>
            <div className="mt-0.5 text-dense font-normal text-paper-low">
              {t('customer_manager_360.usage.trend.cmp.no_projection_hint')}
            </div>
          </div>
        )}
      </div>
      <p className="m-0 mt-2 text-micro text-paper-low">
        {calendar
          ? t('customer_manager_360.usage.trend.note.calendar')
          : t('customer_manager_360.usage.trend.note.rolling')}
      </p>
    </Panel>
  );
}

function CmpCell({
  label,
  value,
  dim = false,
  tone = 'neutral',
}: {
  readonly label: string;
  readonly value: string;
  readonly dim?: boolean;
  readonly tone?: 'ok' | 'bad' | 'neutral';
}): JSX.Element {
  return (
    <div className="border-r border-line-soft px-3.5 py-2.5 last:border-r-0">
      <div className="text-micro text-paper-low">{label}</div>
      <div
        className={cx(
          'mt-0.5 font-mono text-body',
          tone === 'ok' ? 'text-ok' : tone === 'bad' ? 'text-bad' : dim ? 'text-paper-low' : 'text-paper',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function Seg<T extends string>({
  ariaLabel,
  options,
  value,
  testIdPrefix,
  onChange,
}: {
  readonly ariaLabel: string;
  readonly options: readonly { readonly id: T; readonly label: string }[];
  readonly value: T;
  readonly testIdPrefix: string;
  readonly onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div role="tablist" aria-label={ariaLabel} className="inline-flex gap-1">
      {options.map((option) => {
        const active = option.id === value;
        return (
          <Button
            key={option.id}
            role="tab"
            aria-selected={active}
            variant={active ? 'secondary' : 'ghost'}
            size="sm"
            className={active ? 'bg-active text-paper' : undefined}
            data-testid={`${testIdPrefix}-${option.id}`}
            onClick={() => {
              onChange(option.id);
            }}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}
